/**
 * Cart Reservation Manager for B2B Store - Backend Only Version
 * Manages temporary product reservations with timer-based expiry
 * Uses backend API for cross-customer reservation management
 */

console.log('🔧 [Reservation Manager] Initializing Cart Reservation Manager (Backend Only)...');

class CartReservationManager {
  constructor() {
    console.log('🏗️ [Reservation Manager] Constructor called (Backend Only Mode)');
    
    // Default timer duration in milliseconds (30 minutes)
    this.defaultTimerDuration = 30 * 60 * 1000;
    
    // Get timer duration from theme settings or use default
    this.timerDuration = this.getTimerDurationFromSettings();
    
    console.log(`⏱️ [Reservation Manager] Timer duration: ${this.timerDuration / 1000}s (${this.timerDuration / 60000} min)`);
    
    this.customerEmail = this.getCustomerEmail();
    console.log(`👤 [Reservation Manager] Customer: ${this.customerEmail || '❌ NOT LOGGED IN'}`);
    
    if (!this.customerEmail) {
      console.error('❌ [Reservation Manager] CRITICAL: No customer email - reservations will not work!');
    }
    
    this.init();
  }

  async init() {
    console.log('🚀 [Reservation Manager] Initializing reservation system...');
    
    // Wait for API client to be ready
    if (!window.cartReservationAPI) {
      console.warn('⚠️ [Reservation Manager] Waiting for API client...');
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Sync existing cart items with reservations on page load
    await this.syncExistingCart();
    
    // Initialize cached timer data
    this.cachedTimer = null;
    
    // Start timer intervals (visual every second, backend every minute)
    this.startTimerInterval();
    
    // Listen for cart updates to extend timer
    this.listenForCartUpdates();
    
    console.log('✅ [Reservation Manager] Initialization complete');
  }
  
  /**
   * Sync existing cart items with reservations on page load
   * Uses /sync endpoint which completely overrides quantities in DB
   */
  async syncExistingCart() {
    if (!this.customerEmail) {
      console.warn('⚠️ [Reservation Manager] Cannot sync cart - no customer email');
      return;
    }
    
    if (!window.cartReservationAPI) {
      console.error('❌ [Reservation Manager] Cannot sync cart - API client not loaded');
      return;
    }
    
    try {
      console.log('🔄 [Reservation Manager] ========================================');
      console.log('🔄 [Reservation Manager] SYNCING EXISTING CART ITEMS');
      console.log('🔄 [Reservation Manager] ========================================');
      
      // Fetch current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      console.log(`📦 [Reservation Manager] Cart has ${cart.item_count} total items`);
      
      if (!cart.items || cart.items.length === 0) {
        console.log('ℹ️ [Reservation Manager] Cart is empty - no sync needed');
        return;
      }
      
      // Group items by variant ID to get TOTAL quantity per variant
      const variantQuantities = new Map();
      
      cart.items.forEach(item => {
        const variantId = item.variant_id.toString();
        const currentQty = variantQuantities.get(variantId) || 0;
        variantQuantities.set(variantId, currentQty + item.quantity);
      });
      
      console.log(`📦 [Reservation Manager] Unique variants in cart: ${variantQuantities.size}`);
      
      // Prepare cart items for sync with inventory limits
      const cartItems = [];
      
      for (const [variantId, quantity] of variantQuantities.entries()) {
        // Try to get inventory limit from DOM
        let inventoryLimit = null;
        const variantDataEl = document.querySelector(`[data-variant-id="${variantId}"]`);
        
        if (variantDataEl?.dataset.inventoryQuantity) {
          inventoryLimit = parseInt(variantDataEl.dataset.inventoryQuantity);
          console.log(`   📌 Variant ${variantId}: ${quantity} units, limit: ${inventoryLimit} (DOM)`);
        } else {
          // Fetch from custom inventory endpoint
          console.log(`   🔍 Variant ${variantId}: Fetching from custom endpoint...`);
          try {
            const cartItem = cart.items.find(i => i.variant_id.toString() === variantId);
            if (cartItem && cartItem.handle) {
              const inventoryResponse = await fetch(`/products/${cartItem.handle}?view=inventory`);
              const productData = await inventoryResponse.json();
              
              const variantData = productData.variants.find(v => v.id.toString() === variantId);
              if (variantData && variantData.inventory_management === 'shopify') {
                inventoryLimit = variantData.inventory_quantity;
                console.log(`   ✅ From custom endpoint - limit: ${inventoryLimit}`);
              }
            }
          } catch (error) {
            console.error(`   ❌ Failed to fetch:`, error);
          }
        }
        
        cartItems.push({ 
          variantId, 
          quantity,
          inventoryLimit
        });
        
        console.log(`   📎 Variant: ${variantId}, Quantity: ${quantity}, Limit: ${inventoryLimit || 'N/A'}`);
      }
      
      // Use the sync endpoint to update all reservations at once
      try {
        const syncResult = await window.cartReservationAPI.syncCart(this.customerEmail, cartItems);
        console.log('✅ [Reservation Manager] All reservations synced via backend');
        
        // Update timer from sync response expiresIn (seconds until expiry)
        if (syncResult.expiresIn !== undefined) {
          console.log(`⏱️ [Reservation Manager] Updating timer from sync response expiresIn: ${syncResult.expiresIn} seconds`);
          this.saveTimerFromExpiresIn(syncResult.expiresIn);
        } else if (syncResult.expiresAt) {
          // Fallback to old method if expiresIn not provided
          console.log(`⏱️ [Reservation Manager] Fallback: Using expiresAt: ${syncResult.expiresAt}`);
          this.saveTimerFromExpiresIn(this.timerDuration / 1000); // Use default duration as fallback
        }
      } catch (syncError) {
        console.error('═══════════════════════════════════════════════════════════');
        console.error('❌ [Reservation Manager] BACKEND REJECTED INITIAL SYNC');
        console.error('   Error:', syncError.message);
        console.error('   Reason: Cart exceeds available inventory');
        console.error('   Action: Showing alert ONLY - no cart modification');
        console.error('═══════════════════════════════════════════════════════════');
        
        // Don't auto-clear, don't reload - just show alert
        // Customer can manually adjust quantities
        const message = window.variantStrings?.cartExceedsInventory || 
          'Some items in your cart exceed available inventory (reserved by other customers). Please adjust quantities. Items are in your cart but not fully reserved.';
        alert(message);
        
        console.log('ℹ️  [Reservation Manager] Cart unchanged - customer should manually adjust');
        
        return; // Stop initialization, but don't clear or reload
      }
      
      // Timer is already updated from sync response expiresAt above
      // Just refresh display if items exist
      if (cartItems.length > 0) {
        console.log('⏱️ [Reservation Manager] Timer updated from sync response');
        this.updateTimerDisplayLocal();
      }
      
      console.log('🔄 [Reservation Manager] ========================================');
      console.log('✅ [Reservation Manager] Cart sync complete!');
      console.log('🔄 [Reservation Manager] ========================================');
    } catch (error) {
      console.error('❌ [Reservation Manager] CRITICAL: Failed to sync existing cart:', error);
      console.error('   Error details:', error.message);
      console.error('   Stack:', error.stack);
    }
  }

  /**
   * Get timer duration from theme settings
   */
  getTimerDurationFromSettings() {
    const settingsDuration = window.themeSettings?.cart_reservation_timer_minutes;
    console.log(`⚙️ [Reservation Manager] Settings duration: ${settingsDuration} minutes`);
    
    if (settingsDuration && !isNaN(settingsDuration)) {
      return parseInt(settingsDuration) * 60 * 1000;
    }
    
    return this.defaultTimerDuration;
  }

  /**
   * Get customer email from Liquid template data
   */
  getCustomerEmail() {
    const email = window.customerEmail;
    console.log(`📧 [Reservation Manager] Customer email from window: ${email || '❌ NOT SET'}`);
    
    if (email && email !== 'null' && email !== 'undefined' && email !== '') {
      return email;
    }
    
    console.warn('⚠️ [Reservation Manager] No valid customer email found!');
    return null;
  }

  /**
   * Get timer data (from localStorage first, then backend)
   */
  async getTimer() {
    // First check localStorage (source of truth from sync response)
    const localStorageKey = `b2b_cart_timer_${this.customerEmail}`;
    const storedTimer = localStorage.getItem(localStorageKey);
    
    if (storedTimer) {
      try {
        const timerData = JSON.parse(storedTimer);
        if (timerData.expiryTimestamp) {
          // expiryTimestamp should be a number from localStorage
          // If somehow it's a string, parse it (this handles legacy data)
          let expiryTimestamp = timerData.expiryTimestamp;
          if (typeof expiryTimestamp === 'string') {
            expiryTimestamp = parseInt(expiryTimestamp, 10) || new Date(expiryTimestamp).getTime();
          }
          
          // Check if timer is still valid
          if (expiryTimestamp > Date.now()) {
            console.log('⏱️ [Reservation Manager] Retrieved timer from localStorage:', timerData);
            return {
              expiryTimestamp,
              customerEmail: this.customerEmail
            };
          } else {
            // Timer expired, remove from localStorage
            localStorage.removeItem(localStorageKey);
            console.log('⏱️ [Reservation Manager] Timer in localStorage expired, removed');
          }
        }
      } catch (error) {
        console.warn('⚠️ [Reservation Manager] Failed to parse localStorage timer:', error);
      }
    }
    
    // Fallback to backend if no localStorage or expired
    if (window.cartReservationAPI) {
      try {
        const timer = await window.cartReservationAPI.getTimer(this.customerEmail);
        console.log('⏱️ [Reservation Manager] Retrieved timer from backend:', timer);
        return timer;
      } catch (error) {
        console.error('❌ [Reservation Manager] Error reading timer from backend:', error);
      }
    }
    
    return null;
  }

  /**
   * Save timer data to localStorage using expiresIn (seconds until expiry)
   * This is the preferred method as it avoids timezone parsing issues
   * @param {number} expiresInSeconds - Number of seconds until expiry
   */
  saveTimerFromExpiresIn(expiresInSeconds) {
    if (!this.customerEmail) {
      console.warn('⚠️ [Reservation Manager] Cannot save timer - no customer email');
      return;
    }
    
    // Calculate expiry based on client's clock + expiresIn from server
    const expiryTimestamp = Date.now() + (expiresInSeconds * 1000);
    
    const minutes = Math.floor(expiresInSeconds / 60);
    const seconds = Math.floor(expiresInSeconds % 60);
    
    console.log('⏱️ [Reservation Manager] Timer from server expiresIn:');
    console.log(`   expiresIn: ${expiresInSeconds} seconds (${minutes}m ${seconds}s)`);
    console.log(`   Client now: ${new Date().toLocaleTimeString()}`);
    console.log(`   Calculated expiry: ${new Date(expiryTimestamp).toLocaleTimeString()}`);
    
    const localStorageKey = `b2b_cart_timer_${this.customerEmail}`;
    const timerData = {
      expiryTimestamp,
      expiresIn: expiresInSeconds,
      customerEmail: this.customerEmail,
      savedAt: Date.now()
    };
    
    localStorage.setItem(localStorageKey, JSON.stringify(timerData));
    console.log('💾 [Reservation Manager] Timer saved to localStorage:', timerData);
    
    // Update cached timer immediately
    this.cachedTimer = {
      expiryTimestamp,
      customerEmail: this.customerEmail
    };
    this.updateTimerDisplayLocal();
  }

  /**
   * Legacy method - Save timer data to localStorage (from sync response expiresAt)
   * @deprecated Use saveTimerFromExpiresIn instead
   */
  saveTimerToLocalStorage(expiresAt) {
    // Fallback to using default timer duration
    console.warn('⚠️ [Reservation Manager] Using legacy saveTimerToLocalStorage - prefer expiresIn');
    this.saveTimerFromExpiresIn(this.timerDuration / 1000);
  }

  /**
   * Check existing timer from localStorage
   * Timer is updated from sync response expiresAt, so we just load it
   */
  async checkAndMaybeExtendTimer() {
    console.log('🔍 [Reservation Manager] Loading timer from localStorage...');
    
    // Get timer from localStorage (source of truth from sync response)
    const timer = await this.getTimer();
    
    if (!timer || !timer.expiryTimestamp) {
      console.log('   ℹ️ No timer in localStorage - will be set on next cart sync');
      this.cachedTimer = null;
      this.updateTimerDisplayLocal();
      return;
    }
    
    // Ensure timestamp is a number (should always be from localStorage now)
    let expiryTimestamp = timer.expiryTimestamp;
    if (typeof expiryTimestamp === 'string') {
      expiryTimestamp = parseInt(expiryTimestamp, 10) || new Date(expiryTimestamp).getTime();
    }
    
    const now = Date.now();
    const timeRemaining = expiryTimestamp - now;
    const minutesRemaining = Math.floor(timeRemaining / 60000);
    
    console.log(`   ⏰ Timer from localStorage expires at: ${new Date(expiryTimestamp).toLocaleTimeString()}`);
    console.log(`   ⏰ Time remaining: ${minutesRemaining} minutes`);
    
    if (timeRemaining <= 0) {
      console.log('   ⏰ Timer expired - clearing');
      await this.handleTimerExpiry();
      return;
    }
    
    // Cache the timer for display
    this.cachedTimer = {
      expiryTimestamp: expiryTimestamp,
      customerEmail: this.customerEmail
    };
    this.updateTimerDisplayLocal();
    
    console.log('   ✅ Timer loaded from localStorage - will be updated on next cart sync');
  }

  /**
   * Start or extend the cart timer using expiresIn from sync response
   * @param {number|null} expiresInSeconds - Seconds until expiry from server, or null to use localStorage/default
   */
  async startOrExtendTimer(expiresInSeconds = null) {
    let expiryTimestamp;
    
    if (expiresInSeconds !== null && expiresInSeconds > 0) {
      // Use expiresIn from server response (in seconds)
      const minutes = Math.floor(expiresInSeconds / 60);
      const seconds = Math.floor(expiresInSeconds % 60);
      console.log(`⏱️ [Reservation Manager] Using expiresIn from server: ${expiresInSeconds}s (${minutes}m ${seconds}s)`);
      expiryTimestamp = Date.now() + (expiresInSeconds * 1000);
    } else {
      // Check localStorage first
      const localStorageKey = `b2b_cart_timer_${this.customerEmail}`;
      const storedTimer = localStorage.getItem(localStorageKey);
      
      if (storedTimer) {
        try {
          const timerData = JSON.parse(storedTimer);
          if (timerData.expiryTimestamp && timerData.expiryTimestamp > Date.now()) {
            expiryTimestamp = typeof timerData.expiryTimestamp === 'string' 
              ? parseInt(timerData.expiryTimestamp, 10)
              : timerData.expiryTimestamp;
            console.log('⏱️ [Reservation Manager] Using timer from localStorage:', new Date(expiryTimestamp).toLocaleTimeString());
          }
        } catch (error) {
          console.warn('⚠️ [Reservation Manager] Failed to parse localStorage timer');
        }
      }
      
      // Fallback: calculate new timer using default duration
      if (!expiryTimestamp) {
        expiryTimestamp = Date.now() + this.timerDuration;
        console.log('⏱️ [Reservation Manager] Calculating new timer with default duration');
      }
    }
    
    console.log('⏱️ [Reservation Manager] Timer expires at:', new Date(expiryTimestamp).toLocaleTimeString());
    
    // Save to localStorage using the calculated expiry
    const remainingSeconds = Math.floor((expiryTimestamp - Date.now()) / 1000);
    this.saveTimerFromExpiresIn(remainingSeconds);
    
    // Publish event for UI updates
    this.publishTimerUpdate();
  }

  /**
   * Update cached timer data and refresh display (called on cart actions)
   */
  async refreshTimerFromBackend() {
    console.log('🔄 [Reservation Manager] Refreshing timer from backend...');
    
    const timer = await this.getTimer();
    this.cachedTimer = timer;
    
    if (timer) {
      console.log(`   ✅ Timer updated - expires: ${new Date(timer.expiryTimestamp).toLocaleTimeString()}`);
    } else {
      console.log('   ℹ️ No timer active');
    }
    
    this.updateTimerDisplayLocal();
  }

  /**
   * Handle timer expiry - clear cart and reservations
   */
  async handleTimerExpiry() {
    console.log('═══════════════════════════════════════════════════════════');
    console.log('🧹 [Reservation Manager] TIMER EXPIRED - CLEARING CART');
    console.log('═══════════════════════════════════════════════════════════');
    
    // Clear cached timer
    this.cachedTimer = null;
    
    // Clear timer from localStorage
    if (this.customerEmail) {
      const localStorageKey = `b2b_cart_timer_${this.customerEmail}`;
      localStorage.removeItem(localStorageKey);
      console.log('✅ [Reservation Manager] Timer removed from localStorage');
    }
    
    // Clear customer's reservations via backend
    if (this.customerEmail && window.cartReservationAPI) {
      try {
        await window.cartReservationAPI.deleteCustomerReservations(this.customerEmail);
        console.log('✅ [Reservation Manager] Backend reservations cleared');
      } catch (error) {
        console.error('❌ [Reservation Manager] Failed to clear backend reservations:', error);
      }
    }
    
    // Clear Shopify cart (entire cart on expiry)
    await this.clearEntireCart();
    
    // Update UI
    this.updateTimerDisplayLocal();
    
    // Show notification to user
    this.showExpiryNotification();
    
    console.log('═══════════════════════════════════════════════════════════');
  }

  /**
   * Clear entire cart (used on timer expiry)
   */
  async clearEntireCart() {
    console.log('🧹 [Reservation Manager] Clearing entire cart...');
    
    try {
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      console.log('✅ [Reservation Manager] Cart cleared');
      const cartClearedMessage = window.cartStrings?.cartCleared || 'Your cart has been cleared.';
      alert(cartClearedMessage);
      setTimeout(() => window.location.reload(), 1000);
      
    } catch (error) {
      console.error('❌ [Reservation Manager] Failed to clear cart:', error);
    }
  }
  
  /**
   * Clear Shopify cart via API
   */
  async clearShopifyCart() {
    console.log('🛒 [Reservation Manager] Clearing Shopify cart...');
    
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        console.log('✅ [Reservation Manager] Cart cleared successfully');
        
        // Refresh page to update cart UI
        setTimeout(() => {
          console.log('🔄 [Reservation Manager] Reloading page...');
          // window.location.reload();
        }, 1500);
      } else {
        console.error('❌ [Reservation Manager] Failed to clear cart:', response.statusText);
      }
    } catch (error) {
      console.error('❌ [Reservation Manager] Error clearing cart:', error);
    }
  }

  /**
   * Show expiry notification to user
   */
  showExpiryNotification() {
    console.log('💬 [Reservation Manager] Showing expiry notification');
    
    const message = window.cartStrings?.reservationExpired || 
                    'Your cart reservation has expired. Your cart has been cleared.';
    
    // Try to show in cart drawer if available
    const cartErrors = document.getElementById('CartDrawer-CartErrors') || 
                      document.getElementById('cart-errors');
    
    if (cartErrors) {
      cartErrors.textContent = message;
      cartErrors.style.display = 'block';
      console.log('✅ [Reservation Manager] Notification displayed in cart');
    } else {
      // Fallback to alert
      alert(message);
      console.log('✅ [Reservation Manager] Notification displayed as alert');
    }
  }

  /**
   * Start interval to check timer and update display
   */
  startTimerInterval() {
    console.log('⏰ [Reservation Manager] Starting timer intervals:');
    console.log('   📺 Visual update: Every 1 second (client-side only)');
    console.log('   🔍 Backend validation: Every 60 seconds');
    
    // Visual timer update every second (no API calls)
    setInterval(() => {
      this.updateTimerDisplayLocal();
    }, 1000);
    
    // Backend validation every minute
    setInterval(async () => {
      console.log('🔍 [Reservation Manager] Minute elapsed - validating with backend...');
      await this.checkTimerExpiryBackend();
    }, 60000); // 60 seconds
    
    // Also check on init
    this.checkTimerExpiryBackend();
  }

  /**
   * Update timer display locally (no API call - called every second)
   */
  updateTimerDisplayLocal() {
    const timerElements = document.querySelectorAll('[data-cart-timer]');
    const timerWrappers = document.querySelectorAll('.cart-timer-wrapper');
    
    if (!timerElements.length) {
      return;
    }
    
    // Use cached timer data (updated only on actions or every minute)
    if (!this.cachedTimer || !this.cachedTimer.expiryTimestamp) {
      timerElements.forEach(el => el.textContent = '');
      timerWrappers.forEach(wrapper => wrapper.style.display = 'none');
      return;
    }
    
    const now = Date.now();
    const timeRemaining = Math.max(0, this.cachedTimer.expiryTimestamp - now);
    
    // If expired, trigger cleanup
    if (timeRemaining === 0) {
      this.handleTimerExpiry();
      return;
    }
    
    const minutes = Math.floor(timeRemaining / 60000);
    const seconds = Math.floor((timeRemaining % 60000) / 1000);
    const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;
    
    timerElements.forEach(el => {
      el.textContent = timeString;
      
      // Styling based on time remaining
      if (minutes < 5) {
        el.classList.add('cart-timer--warning');
        el.style.color = '#d72c0d';
      } else {
        el.classList.remove('cart-timer--warning');
        el.style.color = '#2c662d';
      }
      
      // Pulse animation when less than 1 minute
      if (minutes < 1) {
        el.style.animation = 'pulse 1s ease-in-out infinite';
      } else {
        el.style.animation = 'none';
      }
    });
    
    // Show timer wrappers with color
    timerWrappers.forEach(wrapper => {
      wrapper.style.display = 'block';
      
      if (minutes < 5) {
        wrapper.style.backgroundColor = '#ffe6e6';
        wrapper.style.borderColor = '#ff9999';
      } else {
        wrapper.style.backgroundColor = '#fff9e6';
        wrapper.style.borderColor = '#ffd700';
      }
    });
  }
  
  /**
   * Check timer with backend (called on init and every minute)
   */
  async checkTimerExpiryBackend() {
    console.log('🔍 [Reservation Manager] Validating timer with backend...');
    
    const timer = await this.getTimer();
    
    // Cache the timer data for local updates
    this.cachedTimer = timer;
    
    if (!timer || !timer.expiryTimestamp) {
      console.log('ℹ️ [Reservation Manager] No active timer from backend');
      this.cachedTimer = null;
      this.updateTimerDisplayLocal();
      return;
    }
    
    // Ensure expiryTimestamp is a number
    let expiryTimestamp = timer.expiryTimestamp;
    if (typeof expiryTimestamp === 'string') {
      expiryTimestamp = new Date(expiryTimestamp).getTime();
      timer.expiryTimestamp = expiryTimestamp;
      this.cachedTimer = timer;
      console.log(`   🔄 Converted string timestamp to number: ${expiryTimestamp}`);
    }
    
    const now = Date.now();
    const timeRemaining = expiryTimestamp - now;
    
    console.log(`⏱️ [Reservation Manager] Backend timer check:`);
    console.log(`   Expires at: ${new Date(expiryTimestamp).toLocaleTimeString()}`);
    console.log(`   Current time: ${new Date(now).toLocaleTimeString()}`);
    console.log(`   Time remaining: ${Math.floor(timeRemaining / 1000)} seconds (${Math.floor(timeRemaining / 60000)} minutes)`);
    
    if (timeRemaining <= 0) {
      console.warn('⏰ [Reservation Manager] Timer expired! Clearing cart...');
      await this.handleTimerExpiry();
    }
  }

  /**
   * Reserve/sync cart after add to cart (syncs entire cart state)
   * Uses /api/reservations/sync endpoint per specification
   * @param {string} variantId - Variant ID to reserve
   * @param {number} quantity - Quantity to reserve
   * @param {HTMLElement} productFormInstance - Optional product-form element instance for error display
   */
  async reserveQuantity(variantId, quantity, productFormInstance = null) {
    console.log(`🔒 [Reservation Manager] ========================================`);
    console.log(`🔒 [Reservation Manager] SYNCING CART AFTER ADD TO CART`);
    console.log(`🔒 [Reservation Manager] Added Variant: ${variantId}`);
    console.log(`🔒 [Reservation Manager] Added Quantity: ${quantity}`);
    console.log(`🔒 [Reservation Manager] Customer: ${this.customerEmail}`);
    
    if (!this.customerEmail) {
      console.error('❌ [Reservation Manager] FAILED: Customer not logged in');
      console.log(`🔒 [Reservation Manager] ========================================`);
      return false;
    }
    
    if (!window.cartReservationAPI) {
      console.error('❌ [Reservation Manager] FAILED: API client not loaded');
      console.log(`🔒 [Reservation Manager] ========================================`);
      return false;
    }
    
    try {
      // Wait a moment for Shopify cart to update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Fetch current cart to get TOTAL quantities
      console.log('📦 [Reservation Manager] Fetching current cart state...');
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      console.log('📦 [Reservation Manager] Cart response:', cart);
      console.log('📦 [Reservation Manager] First item:', cart.items?.[0]);
      
      // Group by variant to get totals
      const variantQuantities = new Map();
      cart.items.forEach(item => {
        const vId = item.variant_id.toString();
        const currentQty = variantQuantities.get(vId) || 0;
        variantQuantities.set(vId, currentQty + item.quantity);
      });
      
      // Prepare sync data with inventory limits
      const cartItems = [];
      
      for (const [vId, qty] of variantQuantities.entries()) {
        // Try to get inventory limit from DOM
        let inventoryLimit = null;
        const variantDataEl = document.querySelector(`[data-variant-id="${vId}"]`);
        
        if (variantDataEl?.dataset.inventoryQuantity) {
          inventoryLimit = parseInt(variantDataEl.dataset.inventoryQuantity);
          console.log(`   📊 Variant ${vId}: ${qty} units, limit: ${inventoryLimit} (DOM)`);
        } else {
          // Fetch from custom inventory endpoint
          console.log(`   🔍 Variant ${vId}: Fetching from custom endpoint...`);
          try {
            const cartItem = cart.items.find(i => i.variant_id.toString() === vId);
            console.log(`      Cart item:`, cartItem);
            
            if (cartItem) {
              // Try handle property
              let productHandle = cartItem.handle || cartItem.product_handle;
              
              // If no handle, extract from URL
              if (!productHandle && cartItem.url) {
                const urlMatch = cartItem.url.match(/\/products\/([^?]+)/);
                if (urlMatch) {
                  productHandle = urlMatch[1];
                  console.log(`      Extracted handle from URL: ${productHandle}`);
                }
              }
              
              if (productHandle) {
                console.log(`      Using handle: ${productHandle}`);
                const inventoryResponse = await fetch(`/products/${productHandle}?view=inventory`);
                const productData = await inventoryResponse.json();
                
                console.log(`      Product data:`, productData);
                
                const variantData = productData.variants.find(v => v.id.toString() === vId);
                if (variantData && variantData.inventory_management === 'shopify') {
                  inventoryLimit = variantData.inventory_quantity;
                  console.log(`   ✅ From custom endpoint - limit: ${inventoryLimit}`);
                } else {
                  console.warn(`      Variant not found or not managed`);
                }
              } else {
                console.warn(`      No handle found for variant ${vId}`);
              }
            } else {
              console.warn(`      Cart item not found for variant ${vId}`);
            }
          } catch (error) {
            console.error(`   ❌ Failed to fetch:`, error);
          }
        }
        
        cartItems.push({ 
          variantId: vId, 
          quantity: qty,
          inventoryLimit
        });
        
        console.log(`   📎 Variant: ${vId}, Quantity: ${qty}, Limit: ${inventoryLimit || 'N/A'}`);
      }
      
      // CRITICAL: Double-check availability BEFORE syncing
      // This is a safety check in case backend validation fails
      console.log('🔍 [Reservation Manager] Double-checking availability before sync...');
      for (const item of cartItems) {
        if (item.inventoryLimit) {
          const reservedByOthers = await window.cartReservationAPI.getReservedQuantity(
            item.variantId,
            this.customerEmail
          );
          
          // Get current quantity in cart for this variant
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          let currentInCart = 0;
          cart.items.forEach(cartItem => {
            if (cartItem.variant_id.toString() === item.variantId.toString()) {
              currentInCart += cartItem.quantity;
            }
          });
          
          const totalAfter = currentInCart;
          const available = item.inventoryLimit - reservedByOthers;
          
          console.log(`   📊 Variant ${item.variantId}:`);
          console.log(`      Inventory Limit: ${item.inventoryLimit}`);
          console.log(`      Reserved by Others: ${reservedByOthers}`);
          console.log(`      Current in Cart: ${currentInCart}`);
          console.log(`      Available: ${available}`);
          console.log(`      Total After: ${totalAfter}`);
          
          if (totalAfter > available) {
            console.error('═══════════════════════════════════════════════════════════');
            console.error('❌ [Reservation Manager] PRE-SYNC VALIDATION FAILED!');
            console.error(`   Variant ${item.variantId}: ${totalAfter} > ${available}`);
            console.error('   Action: Removing item from cart silently');
            console.error('═══════════════════════════════════════════════════════════');
            
            // Remove violating item from cart silently
            const lineItem = cart.items.find(i => i.variant_id.toString() === item.variantId.toString());
            if (lineItem) {
              const lineNumber = cart.items.indexOf(lineItem) + 1;
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ line: lineNumber, quantity: 0 })
              });
              
              // DON'T publish cartUpdate event - don't want to open cart or update counter
              // Item removed silently
            }
            
            // Show message via product form if available
            const productForm = productFormInstance || document.querySelector('product-form');
            if (productForm && productForm.handleErrorMessage) {
              const translationTemplate = window.variantStrings?.reservedByOthersWithCart || 
                'Only [available] available ([cart] already in cart). [reserved] reserved by others.';
              const message = translationTemplate
                .replace('[available]', available)
                .replace('[cart]', currentInCart)
                .replace('[reserved]', reservedByOthers);
              productForm.handleErrorMessage(message);
              
              // Re-enable button
              const submitButton = productForm.querySelector('[type="submit"]');
              if (submitButton) {
                submitButton.removeAttribute('aria-disabled');
                submitButton.classList.remove('loading');
                const spinner = productForm.querySelector('.loading__spinner');
                if (spinner) spinner.classList.add('hidden');
              }
            }
            
            return false;
          }
        }
      }
      
      // Sync entire cart with backend (with server-side validation)
      console.log('🔄 [Reservation Manager] Syncing entire cart with backend...');
      
      try {
        const syncResult = await window.cartReservationAPI.syncCart(this.customerEmail, cartItems);
        
        // Additional validation: check if sync response indicates violations
        // Even if HTTP 200, backend might return success=true but with exceeded values
        if (syncResult.syncedItems && Array.isArray(syncResult.syncedItems)) {
          const hasViolations = syncResult.syncedItems.some(item => {
            // Check if exceeded is positive (means violation)
            if (item.exceeded && item.exceeded > 0) {
              return true;
            }
            // Also check if totalReserved + requestedQuantity > inventoryLimit
            if (item.inventoryLimit && item.totalReserved && item.requestedQuantity) {
              const totalAfter = item.totalReserved + item.requestedQuantity;
              if (totalAfter > item.inventoryLimit) {
                return true;
              }
            }
            return false;
          });
          
          if (hasViolations) {
            console.error('═══════════════════════════════════════════════════════════');
            console.error('❌ [Reservation Manager] VIOLATIONS DETECTED IN SYNC RESPONSE!');
            console.error('   Response:', syncResult);
            console.error('   Action: Removing items from cart');
            console.error('═══════════════════════════════════════════════════════════');
            
            // Remove violating items from cart
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();
            
            for (const violationItem of syncResult.syncedItems) {
              if (violationItem.exceeded > 0 || 
                  (violationItem.inventoryLimit && 
                   violationItem.totalReserved + violationItem.requestedQuantity > violationItem.inventoryLimit)) {
                
                const lineItem = cart.items.find(item => 
                  item.variant_id.toString() === violationItem.variantId.toString()
                );
                
                if (lineItem) {
                  const lineNumber = cart.items.indexOf(lineItem) + 1;
                  await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({ line: lineNumber, quantity: 0 })
                  });
                  
                  console.log(`   🗑️ Removed variant ${violationItem.variantId} from cart`);
                }
              }
            }
            
            // DON'T update cart UI - we removed item silently
            // DON'T publish cartUpdate event - don't want to open cart or update counter
            
            // Show message via product form if available
            const productForm = productFormInstance || document.querySelector('product-form');
            if (productForm && productForm.handleErrorMessage) {
              const violation = syncResult.syncedItems[0];
              const available = Math.max(0, violation.inventoryLimit - violation.totalReserved);
              const translationTemplate = window.variantStrings?.reservedByOthersWithCart || 
                'Only [available] available ([cart] already in cart). [reserved] reserved by others.';
              const message = translationTemplate
                .replace('[available]', available)
                .replace('[cart]', violation.requestedQuantity || 0)
                .replace('[reserved]', violation.totalReserved);
              productForm.handleErrorMessage(message);
            }
            
            return false;
          }
        }
        
        // Update timer using expiresIn from sync response (seconds until expiry)
        if (syncResult.expiresIn !== undefined) {
          console.log(`⏱️ [Reservation Manager] Updating timer from sync response expiresIn: ${syncResult.expiresIn} seconds`);
          await this.startOrExtendTimer(syncResult.expiresIn);
        } else {
          console.log(`⏱️ [Reservation Manager] No expiresIn in sync response, using localStorage or calculating new`);
          await this.startOrExtendTimer();
        }
        
        console.log('✅ [Reservation Manager] CART SYNCED SUCCESSFULLY');
        console.log(`🔒 [Reservation Manager] ========================================`);
        return true;
        
      } catch (syncError) {
        console.error('═══════════════════════════════════════════════════════════');
        console.error('❌ [Reservation Manager] BACKEND REJECTED SYNC');
        console.error('   Error:', syncError.message);
        console.error('   Reason: Inventory limit exceeded');
        console.error('   Action: Removing item from cart and reloading');
        console.error('═══════════════════════════════════════════════════════════');
        
        // Backend rejected - remove the just-added item from cart
        try {
          console.log('🗑️ [Reservation Manager] Removing item from cart...');
          
          // Get current cart to find the line item
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          
          // Find the line item for this variant
          const lineItem = cart.items.find(item => item.variant_id.toString() === variantId.toString());
          
          if (lineItem) {
            // Remove using line number (Shopify cart lines are 1-based)
            const lineNumber = cart.items.indexOf(lineItem) + 1;
            
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ line: lineNumber, quantity: 0 })
            });
            
            console.log('✅ [Reservation Manager] Item removed from cart');
            
            // DON'T update cart UI - we removed item silently
            // DON'T publish cartUpdate event - don't want to open cart or update counter
            
            // Show error message via product form if available
            const productForm = productFormInstance || document.querySelector('product-form');
            if (productForm && productForm.handleErrorMessage) {
              let errorMessage = window.variantStrings?.notAvailableGeneric || 'This product is not available in the requested quantity.';
              
              if (syncError.violations && syncError.violations.length > 0) {
                const violation = syncError.violations[0];
                const available = Math.max(0, violation.inventoryLimit - violation.totalReserved);
                const translationTemplate = window.variantStrings?.reservedByOthersWithCart || 
                  'Only [available] available ([cart] already in cart). [reserved] reserved by others.';
                errorMessage = translationTemplate
                  .replace('[available]', available)
                  .replace('[cart]', violation.requestedQuantity || 0)
                  .replace('[reserved]', violation.totalReserved);
              }
              
              productForm.handleErrorMessage(errorMessage);
              
              // Re-enable button
              const submitButton = productForm.querySelector('[type="submit"]');
              if (submitButton) {
                submitButton.removeAttribute('aria-disabled');
                submitButton.classList.remove('loading');
                const spinner = productForm.querySelector('.loading__spinner');
                if (spinner) spinner.classList.add('hidden');
              }
            }
          } else {
            console.warn('⚠️ [Reservation Manager] Could not find line item to remove');
            // Show message via product form if available
            const productForm = productFormInstance || document.querySelector('product-form');
            if (productForm && productForm.handleErrorMessage) {
              productForm.handleErrorMessage(window.variantStrings?.notAvailableReviewCart || 'This product is not available in the requested quantity. Please review your cart.');
            }
          }
        } catch (removeError) {
          console.error('❌ [Reservation Manager] Failed to remove item:', removeError);
          // Show message via product form if available
          const productForm = productFormInstance || document.querySelector('product-form');
          if (productForm && productForm.handleErrorMessage) {
            productForm.handleErrorMessage(window.variantStrings?.notAvailableManualAdjust || 'This product is not available in the requested quantity. Please manually adjust your cart.');
          }
        }
        
        return false;
      }
      
    } catch (error) {
      console.error('❌ [Reservation Manager] FAILED to sync cart:', error);
      console.error('   Error message:', error.message);
      console.log(`🔒 [Reservation Manager] ========================================`);
      return false;
    }
  }

  /**
   * Get total reserved quantity for a variant (excluding current customer)
   */
  async getReservedQuantity(variantId, excludeCurrentCustomer = true) {
    console.log(`📊 [Reservation Manager] ========================================`);
    console.log(`📊 [Reservation Manager] GETTING RESERVED QUANTITY`);
    console.log(`📊 [Reservation Manager] Variant: ${variantId}`);
    console.log(`📊 [Reservation Manager] Exclude current: ${excludeCurrentCustomer}`);
    console.log(`📊 [Reservation Manager] Current customer: ${this.customerEmail}`);
    
    if (!window.cartReservationAPI) {
      console.error('❌ [Reservation Manager] API client not loaded!');
      console.log(`📊 [Reservation Manager] ========================================`);
      return 0;
    }
    
    try {
      const excludeEmail = excludeCurrentCustomer ? this.customerEmail : null;
      console.log(`   🔍 Will exclude from count: ${excludeEmail || 'none'}`);
      
      const totalReserved = await window.cartReservationAPI.getReservedQuantity(
        variantId,
        excludeEmail
      );
      
      console.log(`📊 [Reservation Manager] RESULT: ${totalReserved} units reserved`);
      console.log(`📊 [Reservation Manager] ========================================`);
      
      return totalReserved;
    } catch (error) {
      console.error('❌ [Reservation Manager] Failed to get reserved quantity:', error);
      console.log(`📊 [Reservation Manager] ========================================`);
      // Fail-closed: propagate error so frontend can block
      throw error;
    }
  }

  /**
   * Get system state for debugging
   */
  async getState() {
    console.log('🔍 [Reservation Manager] Getting system state...');
    
    const stats = await window.cartReservationAPI?.getStats();
    
    return {
      customerEmail: this.customerEmail,
      timerDuration: this.timerDuration,
      backendStats: stats
    };
  }

  /**
   * Calculate available quantity for purchase
   */
  async getAvailableQuantity(variantId, inventoryQuantity) {
    console.log(`🧮 [Reservation Manager] ========================================`);
    console.log(`🧮 [Reservation Manager] CALCULATING AVAILABLE QUANTITY`);
    console.log(`🧮 [Reservation Manager] Variant: ${variantId}`);
    console.log(`🧮 [Reservation Manager] Total Inventory: ${inventoryQuantity}`);
    
    const reservedByOthers = await this.getReservedQuantity(variantId, true);
    const available = Math.max(0, inventoryQuantity - reservedByOthers);
    
    console.log(`🧮 [Reservation Manager] ----------------------------------------`);
    console.log(`🧮 [Reservation Manager] Reserved by others: ${reservedByOthers}`);
    console.log(`🧮 [Reservation Manager] AVAILABLE FOR THIS CUSTOMER: ${available}`);
    console.log(`🧮 [Reservation Manager] ========================================`);
    
    return available;
  }

  /**
   * Listen for cart updates to extend timer
   */
  listenForCartUpdates() {
    console.log('👂 [Reservation Manager] Listening for cart updates...');
    
    // Subscribe to PUB_SUB_EVENTS if available
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
        console.log('🛒 [Reservation Manager] Cart update detected:', event);
        this.handleCartUpdate(event);
      });
    }
    
    // Also observe cart changes via PerformanceObserver
    if (window.PerformanceObserver) {
      const cartObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(entry.initiatorType);
          const isResponseOk = entry.responseStatus === undefined || entry.responseStatus === 200;
          
          const cartURLs = [
            '/cart/add',
            '/cart/add.js',
            '/cart/change',
            '/cart/change.js',
            '/cart/update',
            '/cart/update.js'
          ];
          
          if (isValidRequestType && isResponseOk && cartURLs.some(url => entry.name.includes(url))) {
            console.log('🔍 [Reservation Manager] Cart API call detected:', entry.name);
            console.log('   ⏱️ Timer will be updated from sync response expiresAt');
            // Timer will be updated from sync response expiresAt in reserveQuantity method
            // No need to call startOrExtendTimer here
          }
        });
      });
      
      cartObserver.observe({ entryTypes: ['resource'] });
    }
  }

  /**
   * Handle cart update event
   * Timer will be updated from sync response expiresAt, so we just refresh display
   */
  async handleCartUpdate(event) {
    console.log('🔄 [Reservation Manager] Cart updated - refreshing timer from localStorage...');
    
    // Timer is updated from sync response, so just refresh from localStorage
    const timer = await this.getTimer();
    if (timer) {
      this.cachedTimer = timer;
      this.updateTimerDisplayLocal();
    }
  }

  /**
   * Publish timer update event
   */
  publishTimerUpdate() {
    const event = new CustomEvent('cart-timer-update', {
      detail: {
        timer: this.cachedTimer
      }
    });
    
    document.dispatchEvent(event);
  }
}

// Initialize the manager
window.cartReservationManager = new CartReservationManager();

console.log('✅ [Reservation Manager] Cart Reservation Manager loaded');
console.log('💡 [Reservation Manager] Use window.cartReservationManager to access the manager');
console.log('💡 [Reservation Manager] Use window.testReservations() to prefill test data');

/**
 * Test command to prefill localStorage with sample data
 */
window.testReservations = function(options = {}) {
  console.log('🧪 [Test Command] Prefilling localStorage with test data...');
  
  const {
    variantIds = [12345, 67890, 11111],
    testEmails = ['test1@example.com', 'test2@example.com', 'test3@example.com'],
    quantities = [5, 10, 3],
    timerMinutes = 30
  } = options;
  
  // Clear existing data
  localStorage.removeItem('b2b_cart_reservations');
  localStorage.removeItem('b2b_cart_timer');
  console.log('🧹 [Test Command] Cleared existing data');
  
  // Create test reservations
  const reservations = {};
  
  variantIds.forEach((variantId, index) => {
    reservations[variantId] = {};
    
    testEmails.forEach((email, emailIndex) => {
      const quantity = quantities[emailIndex % quantities.length] || 5;
      reservations[variantId][email] = {
        quantity: quantity,
        timestamp: Date.now()
      };
      console.log(`📦 [Test Command] Added reservation: variant ${variantId}, ${email}, ${quantity} units`);
    });
  });
  
  localStorage.setItem('b2b_cart_reservations', JSON.stringify(reservations));
  console.log('✅ [Test Command] Reservations created');
  
  // Set timer
  const expiryTimestamp = Date.now() + (timerMinutes * 60 * 1000);
  const timerData = {
    expiryTimestamp,
    customerEmail: window.customerEmail || 'current@customer.com'
  };
  localStorage.setItem('b2b_cart_timer', JSON.stringify(timerData));
  console.log(`⏱️ [Test Command] Timer set for ${timerMinutes} minutes`);
  
  // Log summary
  console.log('\n📊 [Test Command] Test Data Summary:');
  console.log('   Variants:', variantIds);
  console.log('   Emails:', testEmails);
  console.log('   Quantities:', quantities);
  console.log(`   Timer expires at: ${new Date(expiryTimestamp).toLocaleTimeString()}`);
  
  // Reload to apply changes
  console.log('\n🔄 [Test Command] Reloading page to apply changes...');
  // setTimeout(() => window.location.reload(), 1000);
};

console.log('💡 [Test Command] Usage examples:');
console.log('   window.testReservations()');
console.log('   window.testReservations({ timerMinutes: 5 })');
console.log('   window.testReservations({ variantIds: [123, 456], quantities: [10, 20] })');

